import { forwardRef, useEffect, useImperativeHandle, useRef, type HTMLAttributes } from 'react'

import { attachRowWheel } from '../utils/rowWheel'

/**
 * Горизонтальный ряд со скроллом «как в селекте»: щелчок колеса мыши едет по X
 * и ряд можно протянуть, захватив мышью. Нужен рядам со скрытым скроллбаром —
 * на Windows-мыши без горизонтальной прокрутки их иначе нечем листать
 * (жалоба владельца: «Инструменты» на главной не листались колесиком).
 *
 * Колесо — общее правило utils/rowWheel (то же у рельсы SectionNav): щелчок
 * мыши листает ряд, пока есть куда ехать, жест трекпада уходит странице —
 * иначе страница «подтормаживала» над лентой (владелец 30.09).
 * Тач не трогаем: там нативный скролл. Клик, родившийся из перетаскивания,
 * глушится — отпускание мыши над карточкой не должно её открывать.
 */
const DragScrollRow = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(function DragScrollRow(
	{ children, ...rest },
	outerRef,
) {
	const ref = useRef<HTMLDivElement>(null)
	// Ряд нужен снаружи (кнопки слайдера считают по нему края и листают) —
	// отдаём тот же узел, на котором висят жесты.
	useImperativeHandle(outerRef, () => ref.current as HTMLDivElement, [])

	useEffect(() => {
		const el = ref.current
		if (!el) return

		const offWheel = attachRowWheel(el)

		let down = false
		let moved = false
		let startX = 0
		let startLeft = 0
		const onDown = (e: PointerEvent) => {
			if (e.pointerType !== 'mouse' || e.button !== 0) return
			down = true
			moved = false
			startX = e.clientX
			startLeft = el.scrollLeft
		}
		const onMove = (e: PointerEvent) => {
			if (!down) return
			const dx = e.clientX - startX
			// Порог 4px отделяет клик от протяжки: без него дрожание руки на
			// карточке превращало каждый клик в микро-скролл.
			if (!moved && Math.abs(dx) > 4) {
				moved = true
				el.setPointerCapture(e.pointerId)
				el.style.cursor = 'grabbing'
				el.style.userSelect = 'none'
			}
			if (moved) {
				el.scrollLeft = startLeft - dx
				e.preventDefault()
			}
		}
		const onUp = (e: PointerEvent) => {
			if (!down) return
			down = false
			if (moved) {
				if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
				el.style.cursor = ''
				el.style.userSelect = ''
				// Синтетический click прилетает сразу после pointerup — флаг
				// доживает до него и сбрасывается следующей задачей.
				window.setTimeout(() => {
					moved = false
				}, 0)
			}
		}
		const onClick = (e: MouseEvent) => {
			if (!moved) return
			e.preventDefault()
			e.stopPropagation()
		}
		// Нативное перетаскивание картинок/ссылок рвёт захват — глушим на
		// время протяжки.
		const onDragStart = (e: DragEvent) => {
			if (down || moved) e.preventDefault()
		}
		el.addEventListener('pointerdown', onDown)
		el.addEventListener('pointermove', onMove)
		el.addEventListener('pointerup', onUp)
		el.addEventListener('pointercancel', onUp)
		el.addEventListener('click', onClick, true)
		el.addEventListener('dragstart', onDragStart)
		return () => {
			offWheel()
			el.removeEventListener('pointerdown', onDown)
			el.removeEventListener('pointermove', onMove)
			el.removeEventListener('pointerup', onUp)
			el.removeEventListener('pointercancel', onUp)
			el.removeEventListener('click', onClick, true)
			el.removeEventListener('dragstart', onDragStart)
		}
	}, [])

	return (
		<div ref={ref} {...rest}>
			{children}
		</div>
	)
})

export default DragScrollRow
